import {
    DiscoveryApi,
    FetchApi,
} from '@backstage/core-plugin-api'
import { createApiRef } from '@backstage/core-plugin-api'

export type Announcement = {
    id: string
    title: string
    message: string
    severity: 'info' | 'warning' | 'error' | 'success'
    category?: string
    tags?: string[]
    active: boolean
    start_date?: string
    end_date?: string
    created_at: string
    created_by?: string
}

export const announcementsApiRef = createApiRef<AnnouncementsApiClient>({
    id: 'plugin.announcements.service',
})

export class AnnouncementsApiClient {
    constructor(
        private readonly discoveryApi: DiscoveryApi,
        private readonly fetchApi: FetchApi,
    ) { }

    async list(): Promise<Announcement[]> {
        const baseUrl = await this.discoveryApi.getBaseUrl('announcements')

        const response = await this.fetchApi.fetch(
            `${baseUrl}/announcements`,
        )

        if (!response.ok) {
            throw new Error('Failed to fetch announcements')
        }

        return response.json()
    }

    async create(announcement: Omit<Announcement, 'id' | 'created_at'>, user: string): Promise<Announcement> {
        const baseUrl = await this.discoveryApi.getBaseUrl('announcements')

        const response = await this.fetchApi.fetch(
            `${baseUrl}/announcements/${user}`,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(announcement),
            },
        )

        if (!response.ok) {
            throw new Error('Failed to create announcement')
        }

        return response.json()
    }

    async update(id: string, announcement: Omit<Announcement, 'id' | 'created_at'>): Promise<Announcement> {
        const baseUrl = await this.discoveryApi.getBaseUrl('announcements')

        const response = await this.fetchApi.fetch(
            `${baseUrl}/announcements/${id}`,
            {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(announcement),
            },
        )

        if (!response.ok) {
            throw new Error('Failed to update announcement')
        }

        return response.json()
    }

    async delete(id: string): Promise<void> {
        const baseUrl = await this.discoveryApi.getBaseUrl('announcements')

        const response = await this.fetchApi.fetch(
            `${baseUrl}/announcements/${id}`,
            {
                method: 'DELETE',
            },
        )

        if (!response.ok) {
            throw new Error('Failed to delete announcement')
        }
    }
}